'use client'

import Image from 'next/image'
import { Session } from 'next-auth'
import { AppLogo } from '@/app/web/ui'
import {
  Button,
  Checkbox,
  CustomFlowbiteTheme,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from 'flowbite-react'
import { useState } from 'react'
import UserIcon from '@/public/images/icons/user.svg'
import { TERMS_OF_SERVICE_URL } from '@/constants/legal'
import { ACCOUNT_URL } from '@/constants/stackup'

interface AuthorizationModalProps {
  session?: Session | null
  confirm: () => void
  closeModal: () => void
}

const AuthorizationModal = ({
  session,
  confirm,
  closeModal,
}: AuthorizationModalProps) => {
  const [openModal, setOpenModal] = useState(true)
  const [isAgree, setIsAgree] = useState(false)

  const customModalTheme: CustomFlowbiteTheme['modal'] = {
    content: {
      base: "relative h-full w-full md:h-auto p-0 sm:p-4",
      inner:
        'relative h-full sm:h-auto rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col w-full',
    },
    header: {
      base: 'flex justify-center rounded-t p-6 border-b-none w-full',
      close: {
        base: 'hidden',
        icon: 'hidden',
      },
    },
    body: {
      base: 'flex-1 overflow-auto p-6',
    },
  }

  return (
    <Modal
      theme={customModalTheme}
      dismissible
      show={openModal}
      initialFocus={-1}
    >
      <ModalHeader>
        <div className="flex items-center gap-2 ml-4 md:ml-0">
          <AppLogo />
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-y-6 text-center">
          <div className="w-full justify-center">
            <h1 className="text-2xl font-bold mb-5 m-auto">
              Confirm your details
            </h1>
            <p>The following information is required by Hackathon</p>
          </div>
          <div className="flex w-full justify-center">
            <div className="flex gap-x-4 rounded-lg bg-gray-50 py-2 px-4 text-sm items-center">
              <Image
                src={session?.user.image ?? UserIcon}
                alt="user image"
                width={40}
                height={40}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
              <div className="flex flex-col">
                <label>
                  <span>{session?.user.firstName}</span>{' '}
                  <span>{session?.user.lastName}</span>
                </label>
                <label className="text-gray-500">
                  @{session?.user.username}
                </label>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-baseline gap-y-2">
            <p className="text-xs">Information you&rsquo;re sharing</p>
            <div className="flex flex-col p-4 gap-y-4 rounded-lg bg-gray-50 w-full items-baseline text-sm">
              <div>
                <p className="font-bold text-gray-700">
                  AngelHack Passport Information
                </p>
                <ul className="flex flex-col items-baseline list-disc ml-4 mt-2">
                  <li>Username</li>
                  <li>First Name, Last Name</li>
                </ul>
              </div>
              <div>
                <p className="font-bold text-gray-700">
                  Information not visible to others
                </p>
                <ul className="flex flex-col items-baseline list-disc ml-4 mt-2">
                  <li>Email</li>
                  <li>Country of Residence</li>
                  <li>Timezone</li>
                </ul>
              </div>
              <div className="w-full text-left">
                <p className="font-bold text-gray-700">Additional Information</p>
                <ul className="flex flex-col items-baseline list-disc ml-4 mt-2">
                  <li>Gender: {session?.user.gender ?? 'N/A'}</li>
                  <li>Birthdate: {session?.user.birthdate ?? 'N/A'}</li>
                  <li>Nationality: {session?.user.nationality ?? 'N/A'}</li>
                  <li>Career Level: {session?.user.careerLevel ?? 'N/A'}</li>
                  <li>Expertise Areas: {session?.user.expertiseAreas?.join(', ') ?? 'N/A'}</li>
                  <li>Tech Stack: {session?.user.techStack?.join(', ') ?? 'N/A'}</li>
                </ul>
              </div>
            </div>
          </div>
          <p className="flex items-baseline text-sm text-gray-500">
            You may review your information on&nbsp;
            <a className="underline font-semibold text-gray-900" href={ACCOUNT_URL} target="_blank">
              AngelHack
            </a>
          </p>
          <div className="flex sm:gap-x-2 items-baseline sm:items-center">
            <Checkbox
              id="agree-check"
              checked={isAgree}
              onChange={(e) => setIsAgree(e.target.checked)}
            />
            <Label
              htmlFor="agree-check"
              className="cursor-pointer text-gray-500 text-sm"
            >
              <span>I&rsquo;ve read and agree to Hackathon&rsquo;s</span>&nbsp;
              <a
                className="font-semibold text-gray-900"
                href={TERMS_OF_SERVICE_URL}
                target="_blank"
              >
                Terms of Service
              </a>
            </Label>
          </div>
          <div className="flex gap-4 w-full space-between">
            <Button
              className="border border-gray-200 w-full"
              color={'white'}
              onClick={() => {
                setOpenModal(false)
                closeModal()
              }}
            >
              Cancel
            </Button>
            <Button
              className="w-full disable:opacity-50"
              color={'dark'}
              onClick={() => {
                setOpenModal(false)
                confirm()
              }}
              disabled={!isAgree}
            >
              Confirm
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default AuthorizationModal
